import React from "react";
import { Link } from "react-router-dom";
import { MessageCircle, ImageOff } from "lucide-react";

/** Hero-priority resolver — `image_urls[0]` (admin/scraped hero) wins over
 * legacy `thumbnail_url`, then falls back to `null` so the caller can render
 * a branded placeholder. Exported so pages that render inline `<img>` (e.g.
 * `/buy`) stay in lockstep with the shared card. */
export function resolveHeroImage(project) {
  if (project?.image_urls && project.image_urls.length > 0 && project.image_urls[0]) {
    return project.image_urls[0];
  }
  return project?.thumbnail_url || null;
}

/** Branded "No Image Available" placeholder — used when both `image_urls` and
 * `thumbnail_url` are absent. Kept minimal + on-brand (navy on cream). */
export function NoImagePlaceholder({ label = "No Image Available", className = "" }) {
  return (
    <div
      className={`w-full h-full flex flex-col items-center justify-center bg-sg-cream-soft text-sg-navy/60 ${className}`}
      data-testid="no-image-placeholder"
    >
      <ImageOff className="w-8 h-8 mb-2 opacity-60" strokeWidth={1.5} />
      <div className="text-[10px] font-semibold uppercase tracking-widest">INProp<span className="text-sg-gold-deep">Portal</span></div>
      <div className="text-[11px] text-sg-navy/50 mt-1 text-center px-2">{label}</div>
    </div>
  );
}

/** SG-style project / property card. */
export default function ProjectCard({
  project,
  tag = "NEW LAUNCH",
  inquireHref,
  testIdPrefix = "project-card",
  detailHref,
  onInquire,
}) {
  const priceLabel = formatPrice(project.starting_price_lakhs);
  const heroSrc = resolveHeroImage(project);
  const waMsg = encodeURIComponent(
    `Hi, I'm interested in ${project.project_name} (${project.locality_tag}). Could you share more details?`
  );
  const wa = inquireHref || `https://wa.me/919999999999?text=${waMsg}`;
  const detail = detailHref || `/project/${project.project_id}`;
  return (
    <div className="sg-card flex flex-col" data-testid={`${testIdPrefix}-${project.project_id}`}>
      <Link to={detail} className="block relative aspect-[16/9] overflow-hidden bg-sg-cream-soft" data-testid={`${testIdPrefix}-image-${project.project_id}`}>
        {heroSrc ? (
          <img src={heroSrc} alt={project.project_name} className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" />
        ) : (
          <NoImagePlaceholder />
        )}
        {tag && (
          <span className="absolute top-4 left-4 chip chip-gold" data-testid="project-tag">{tag}</span>
        )}
        <span className="absolute top-4 right-4 chip" data-testid="project-locality-chip">{project.locality_tag}</span>
      </Link>
      <div className="p-6 flex-1 flex flex-col">
        <div className="overline text-sg-gold-deep">{project.developer}</div>
        <Link to={detail} className="block">
          <h3 className="font-display text-xl mt-1 text-sg-navy hover:text-sg-gold-deep transition-colors" data-testid={`${testIdPrefix}-title-${project.project_id}`}>
            {project.project_name}
          </h3>
        </Link>
        <div className="mt-3 flex items-center justify-between border-t border-dashed border-sg-line pt-3 text-sm text-sg-mute">
          <span className="font-mono">{(project.configurations || []).join(" · ")}</span>
          <span className="overline text-sg-gold-deep">MahaCRITI · {project.maharera_id}</span>
        </div>
        <div className="mt-auto pt-5 flex items-end justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-sg-mute">Starting from</div>
            <div className="rupee text-3xl text-sg-navy mt-0.5">{priceLabel}</div>
          </div>
          <a
            href={wa}
            target="_blank"
            rel="noreferrer"
            onClick={onInquire}
            className="inline-flex items-center gap-2 rounded-full bg-sg-navy text-white px-4 py-2.5 text-sm hover:bg-sg-navy-700 transition-colors"
            data-testid={`whatsapp-inquire-${project.project_id}`}
          >
            <MessageCircle className="w-4 h-4 text-sg-gold" /> Inquire via WhatsApp
          </a>
        </div>
        {project.market_trend_data && (
          <div className="mt-4 text-xs text-sg-mute italic border-t border-dashed border-sg-line pt-3">
            <span className="not-italic font-semibold font-mono text-sg-navy">{project.market_trend_data.yoy}</span>
            <span className="mx-1">·</span>{project.market_trend_data.summary}
          </div>
        )}
      </div>
    </div>
  );
}

/** Format Lakhs → "₹ 95 Lac" or "₹ 1.45 Cr". */
export function formatPrice(lakhs) {
  if (!lakhs) return "₹ —";
  if (lakhs >= 100) {
    const cr = (lakhs / 100).toFixed(lakhs % 100 === 0 ? 0 : 2);
    return `₹ ${cr} Cr`;
  }
  return `₹ ${lakhs} Lac`;
}
